import React, { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Input,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  FormControl,
  FormLabel,
  FormHelperText,
  Text,
  useDisclosure,
  useToast,
  InputGroup,
  InputRightElement,
  IconButton,
} from '@chakra-ui/react';
import { Eye, EyeSlash, DotsThreeVertical, PencilSimpleLine, Trash, Star } from 'phosphor-react';
import { Phone, Building2, Shield, Wifi, WifiOff, Plus } from 'lucide-react';
import { useDialog } from '../../providers/dialog';
import waApi from '../../services/whatsapp/waApi';
import { formatPhone } from '../../services/whatsapp/waFormatters';

interface WaNumber {
  id: string;
  label: string;
  phone_number: string;
  phone_number_id: string;
  waba_id: string;
  is_active: boolean;
  is_default: boolean;
  departments: string[];
  created_at: string | null;
}

interface NumberFormData {
  label: string;
  phone_number: string;
  phone_number_id: string;
  waba_id: string;
  api_token: string;
}

const EMPTY_FORM: NumberFormData = {
  label: '',
  phone_number: '',
  phone_number_id: '',
  waba_id: '',
  api_token: '',
};

export default function WaConnectionPage() {
  const toast = useToast();
  const showDialog = useDialog();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [numbers, setNumbers] = useState<WaNumber[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<NumberFormData>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [showToken, setShowToken] = useState(false);
  const [menuOpen, setMenuOpen] = useState<string | null>(null);

  const fetchNumbers = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await waApi.get('/wa-numbers');
      setNumbers(resp.data);
    } catch {
      setNumbers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchNumbers();
  }, [fetchNumbers]);

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setShowToken(false);
    setMenuOpen(null);
    onOpen();
  };

  const openEdit = (num: WaNumber) => {
    setEditingId(num.id);
    setForm({
      label: num.label,
      phone_number: num.phone_number,
      phone_number_id: num.phone_number_id,
      waba_id: num.waba_id,
      api_token: '',
    });
    setShowToken(false);
    setMenuOpen(null);
    onOpen();
  };

  const handleSave = async () => {
    if (!form.label.trim()) {
      toast({ title: 'Nome/Label é obrigatório', status: 'warning', duration: 3000 });
      return;
    }
    if (!form.phone_number_id.trim()) {
      toast({ title: 'Phone Number ID é obrigatório', status: 'warning', duration: 3000 });
      return;
    }
    if (!editingId && !form.api_token.trim()) {
      toast({ title: 'API Token é obrigatório ao cadastrar', status: 'warning', duration: 3000 });
      return;
    }

    setSaving(true);
    try {
      if (editingId) {
        const payload: Record<string, string> = {};
        if (form.label) payload.label = form.label;
        if (form.phone_number) payload.phone_number = form.phone_number;
        if (form.waba_id) payload.waba_id = form.waba_id;
        if (form.api_token) payload.api_token = form.api_token;
        await waApi.put(`/wa-numbers/${editingId}`, payload);
        toast({ title: 'Número atualizado', status: 'success', duration: 3000 });
      } else {
        await waApi.post('/wa-numbers', {
          label: form.label,
          phone_number: form.phone_number,
          phone_number_id: form.phone_number_id,
          waba_id: form.waba_id,
          api_token: form.api_token,
        });
        toast({ title: 'Número cadastrado', status: 'success', duration: 3000 });
      }
      onClose();
      await fetchNumbers();
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      toast({
        title: typeof detail === 'string' ? detail : 'Erro ao salvar',
        status: 'error',
        duration: 4000,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (num: WaNumber) => {
    setMenuOpen(null);
    showDialog({
      status: 'warning',
      title: `Desativar "${num.label}"?`,
      description: 'O número será desativado e não receberá mais mensagens.',
      cancelLabel: 'Cancelar',
      async onConfirm() {
        try {
          await waApi.delete(`/wa-numbers/${num.id}`);
          toast({ title: 'Número desativado', status: 'success', duration: 3000 });
          await fetchNumbers();
        } catch (err: any) {
          const detail = err?.response?.data?.detail;
          toast({
            title: typeof detail === 'string' ? detail : 'Erro ao desativar',
            status: 'error',
            duration: 4000,
          });
        }
      },
    });
  };

  const handleSetDefault = async (num: WaNumber) => {
    setMenuOpen(null);
    try {
      await waApi.put(`/wa-numbers/${num.id}`, { is_default: true });
      toast({ title: `"${num.label}" definido como padrão`, status: 'success', duration: 3000 });
      await fetchNumbers();
    } catch {
      toast({ title: 'Erro ao definir padrão', status: 'error', duration: 3000 });
    }
  };

  return (
    <div className="min-h-screen bg-[#f5f5f7] p-4 sm:p-6 lg:p-8">
      {/* Header */}
      <div className="mb-6 flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 sm:text-3xl">Conexões WhatsApp</h1>
          <p className="mt-1 text-sm text-gray-500">
            Gerencie os números conectados à API oficial da Meta.
          </p>
        </div>
        <button
          type="button"
          onClick={openCreate}
          className="flex items-center justify-center gap-2 rounded-xl bg-green-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-green-700"
        >
          <Plus size={16} />
          Novo Número
        </button>
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex justify-center py-16">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-green-200 border-t-green-600" />
        </div>
      )}

      {/* Empty */}
      {!loading && numbers.length === 0 && (
        <div className="flex flex-col items-center justify-center rounded-2xl bg-white py-20 shadow-sm">
          <div className="mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-green-50">
            <Phone size={40} className="text-green-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-800">Nenhum número cadastrado</h3>
          <p className="mt-1 text-sm text-gray-500">
            Cadastre o primeiro número para conectar ao WhatsApp Business API
          </p>
          <button
            type="button"
            onClick={openCreate}
            className="mt-6 rounded-xl bg-green-600 px-6 py-2.5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-green-700"
          >
            Cadastrar número
          </button>
        </div>
      )}

      {/* Cards Grid */}
      {!loading && numbers.length > 0 && (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {numbers.map(num => (
              <div
                key={num.id}
                className="relative overflow-hidden rounded-2xl border border-gray-200/60 bg-white p-5 shadow-sm transition-all hover:shadow-md"
              >
                {/* Header do card */}
                <div className="mb-4 flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="flex h-11 w-11 items-center justify-center rounded-full bg-green-500 shadow-sm">
                      <Phone size={20} className="text-white" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-bold text-gray-900">
                          {num.label}
                          {num.phone_number
                            ? ` [${num.phone_number.replace(/^55/, '').replace(/(\d{2})(\d+)/, '$1 $2')}]`
                            : ''}
                        </h3>
                      </div>
                      <p className="text-xs text-gray-400">WhatsApp Business API - Meta</p>
                    </div>
                  </div>

                  {/* Menu de ações */}
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setMenuOpen(menuOpen === num.id ? null : num.id)}
                      className="rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
                    >
                      <DotsThreeVertical size={20} weight="bold" />
                    </button>
                    {menuOpen === num.id && (
                      <>
                        {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
                        <div className="fixed inset-0 z-10" onClick={() => setMenuOpen(null)} />
                        <div className="absolute right-0 top-full z-20 mt-1 w-44 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-lg">
                          <button
                            type="button"
                            onClick={() => openEdit(num)}
                            className="flex w-full items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 transition-colors hover:bg-gray-50"
                          >
                            <PencilSimpleLine size={15} />
                            Editar
                          </button>
                          {!num.is_default && (
                            <button
                              type="button"
                              onClick={() => handleSetDefault(num)}
                              className="flex w-full items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 transition-colors hover:bg-gray-50"
                            >
                              <Star size={15} />
                              Definir padrão
                            </button>
                          )}
                          {!num.is_default && (
                            <button
                              type="button"
                              onClick={() => handleDelete(num)}
                              className="flex w-full items-center gap-2.5 px-4 py-2.5 text-sm text-red-600 transition-colors hover:bg-red-50"
                            >
                              <Trash size={15} />
                              Desativar
                            </button>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {/* Info do card */}
                <div className="space-y-2.5">
                  <div className="flex items-center gap-2.5 text-sm text-gray-600">
                    <Phone size={14} className="flex-shrink-0 text-gray-400" />
                    <span>{num.phone_number ? formatPhone(num.phone_number) : '—'}</span>
                  </div>
                  <div className="flex items-center gap-2.5 text-sm text-gray-600">
                    <Building2 size={14} className="flex-shrink-0 text-gray-400" />
                    <span className="truncate">
                      {num.departments?.length > 0
                        ? num.departments.join(', ')
                        : 'Todos os departamentos'}
                    </span>
                  </div>
                  {num.waba_id && (
                    <div className="flex items-center gap-2.5 text-sm text-gray-600">
                      <Shield size={14} className="flex-shrink-0 text-gray-400" />
                      <span className="truncate font-mono text-xs">{num.waba_id}</span>
                    </div>
                  )}
                </div>

                {/* Status */}
                <div className="mt-4 flex items-center justify-between">
                  <div className="flex items-center gap-1.5">
                    {num.is_active ? (
                      <>
                        <Wifi size={13} className="text-green-500" />
                        <span className="text-xs font-medium text-green-600">Conectado</span>
                      </>
                    ) : (
                      <>
                        <WifiOff size={13} className="text-red-400" />
                        <span className="text-xs font-medium text-red-500">Desativado</span>
                      </>
                    )}
                  </div>
                  {num.is_default && (
                    <span className="rounded-full bg-green-100 px-2.5 py-0.5 text-[10px] font-semibold text-green-700">
                      Padrão
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>

          <p className="mt-4 text-center text-sm text-gray-400">
            Mostrando {numbers.length} de {numbers.length} resultados
          </p>
        </>
      )}

      {/* Modal Cadastro/Edicao */}
      <Modal isOpen={isOpen} onClose={onClose} size="lg" isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{editingId ? 'Editar Número' : 'Novo Número WhatsApp'}</ModalHeader>
          <ModalCloseButton />
          <ModalBody display="flex" flexDirection="column" gap={4}>
            <FormControl isRequired>
              <FormLabel>Nome / Label</FormLabel>
              <Input
                value={form.label}
                onChange={e => setForm(f => ({ ...f, label: e.target.value }))}
                placeholder="Ex: Principal, Suporte, Vendas"
              />
            </FormControl>

            <FormControl>
              <FormLabel>Número de Telefone</FormLabel>
              <Input
                value={form.phone_number}
                onChange={e => setForm(f => ({ ...f, phone_number: e.target.value }))}
                placeholder="Ex: +55 48 99999-0000"
              />
            </FormControl>

            <FormControl isRequired>
              <FormLabel>Phone Number ID (Meta)</FormLabel>
              <Input
                value={form.phone_number_id}
                onChange={e => setForm(f => ({ ...f, phone_number_id: e.target.value }))}
                placeholder="ID numérico do Meta Business"
                isDisabled={!!editingId}
                fontFamily="mono"
              />
              {editingId && <FormHelperText>Phone Number ID não pode ser alterado.</FormHelperText>}
            </FormControl>

            <FormControl>
              <FormLabel>Business Account ID (WABA)</FormLabel>
              <Input
                value={form.waba_id}
                onChange={e => setForm(f => ({ ...f, waba_id: e.target.value }))}
                placeholder="ID da conta Business"
                fontFamily="mono"
              />
            </FormControl>

            <FormControl isRequired={!editingId}>
              <FormLabel>
                API Token{' '}
                {editingId && (
                  <Text as="span" fontSize="xs" color="gray.400">
                    (vazio = manter atual)
                  </Text>
                )}
              </FormLabel>
              <InputGroup>
                <Input
                  type={showToken ? 'text' : 'password'}
                  value={form.api_token}
                  onChange={e => setForm(f => ({ ...f, api_token: e.target.value }))}
                  placeholder={
                    editingId
                      ? 'Deixe vazio para manter o token atual'
                      : 'Access Token permanente da Meta'
                  }
                  fontFamily="mono"
                  pr="3rem"
                />
                <InputRightElement>
                  <IconButton
                    aria-label={showToken ? 'Ocultar token' : 'Mostrar token'}
                    icon={showToken ? <EyeSlash size={18} /> : <Eye size={18} />}
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowToken(s => !s)}
                  />
                </InputRightElement>
              </InputGroup>
              <FormHelperText>
                O token será salvo criptografado e nunca será exibido novamente.
              </FormHelperText>
            </FormControl>
          </ModalBody>

          <ModalFooter gap={3}>
            <Button variant="ghost" onClick={onClose}>
              Cancelar
            </Button>
            <Button colorScheme="green" onClick={handleSave} isLoading={saving}>
              {editingId ? 'Salvar' : 'Cadastrar'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  );
}
